import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { EmptyState, ErrorState, Loading } from "../../../components/States";
import { PillView, Toolbar } from "../../../components/ui";
import { date } from "../../../lib/format";
import { ladeBeitragsUebersicht } from "./beitraege-daten";
import {
  FILTER_BUCKET, FILTER_LABEL, beitragPill, filtereBeitraege, fristText, istFilter, uebersichtZeilen, zaehleBeitraege, type BeitragsFilter,
} from "./beitraege-model";
import { useHeute } from "./hooks";
import { Ansichtswahl } from "./bausteine";
import { monthText, nameOfTalent } from "./model";
import "./kooperationen.css";
import { t, ta } from "../../../lib/sprache";

/**
 * Beiträge des ganzen Teams (0068): was diese Woche fällig ist, was überfällig ist, was bei der Marke liegt und was live ist,
 * aber noch keine Prüfung der Werbekennzeichnung hat; je Creator und Marke filterbar. Sichtbar ist, was die Person auch in den
 * Kooperationen sieht (RLS).
 */

const FILTER_REIHE: readonly BeitragsFilter[] = ["offen", "ueberfaellig", "woche", "pruefung", "marke", "kennzeichnung", "alle"];

export function Beitraege() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const today = useHeute();
  const q = useQuery({ queryKey: ["deal_deliverables", orgId, "uebersicht"], queryFn: () => ladeBeitragsUebersicht(api, orgId) });
  const [params, setParams] = useSearchParams();
  const roh = params.get("filter");
  const filter: BeitragsFilter = istFilter(roh) ? roh : "offen";
  const creator = params.get("creator") ?? "";
  const marke = params.get("marke") ?? "";
  const suche = params.get("q") ?? "";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const zeilen = useMemo(() => (q.data ? uebersichtZeilen({ ...q.data, today }) : []), [q.data, today]);
  const vorauswahl = useMemo(() => filtereBeitraege(zeilen, { filter: "alle", talentId: creator, brandId: marke, suche }), [zeilen, creator, marke, suche]);
  const n = zaehleBeitraege(vorauswahl);
  const offenZahl = filtereBeitraege(vorauswahl, { filter: "offen" }).length;
  const gezeigt = useMemo(() => filtereBeitraege(zeilen, { filter, talentId: creator, brandId: marke, suche }), [zeilen, filter, creator, marke, suche]);

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data) return <Loading />;
  const d = q.data;
  const creatorIds = new Set(zeilen.map((z) => z.deal.talent_id));
  const markenIds = new Set(zeilen.map((z) => z.deal.brand_id));
  const zahl = (f: BeitragsFilter) => (f === "offen" ? offenZahl : f === "alle" ? vorauswahl.length : n[FILTER_BUCKET[f]!]);
  const ziel = (f: BeitragsFilter) => {
    const next = new URLSearchParams(params);
    if (f === "offen") next.delete("filter");
    else next.set("filter", f);
    return `?${next.toString()}`;
  };
  const kachel = (f: BeitragsFilter, text: string, ton: string) => (
    <Link key={f} className="panel stack tk-koop-karte" to={ziel(f)} replace aria-current={filter === f ? "true" : undefined}>
      <span className="eyebrow">{text}</span>
      <strong className={`num ${ton}`}>{zahl(f)}</strong>
    </Link>
  );

  return (
    <main id="main" className="content tk-koop">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{t("Beiträge", "Posts")}</h1>
          <p>{ta("Was bis wann als Entwurf kommt, was das Team prüft, was per Freigabe-Link bei der Marke liegt, was live ist und wo die Kennzeichnung noch geprüft werden muss.", "What arrives as a draft by when, what the team reviews, what is with the brand via approval link, what is live and where the ad disclosure still needs checking.", "Was bis wann als Entwurf fertig sein muss, was per Freigabe-Link bei der Marke liegt, was live ist und wo die Kennzeichnung noch geprüft werden muss.", "What needs to be ready as a draft by when, what is with the brand via approval link, what is live and where the ad disclosure still needs checking.")}</p>
        </div>
        <Ansichtswahl aktuell="beitraege">
          <Link className="btn" to="/agentur/kalender">{t("Kalender", "Calendar")}</Link>
        </Ansichtswahl>
      </div>

      {zeilen.length === 0 ? (
        <EmptyState title={t("Noch keine Beiträge angelegt.", "No posts created yet.")} text={t("Beiträge legst du in einer Kooperation an (Block „Beiträge“), einzeln oder mehrere gleiche auf einmal.", "You create posts in a deal (“Posts” block), individually or several identical ones at once.")}
          action={<Link className="btn primary" to="/agentur/kooperationen">{t("Zu den Kooperationen", "To the deals")}</Link>} />
      ) : (
        <>
          <div className="tk-beitraege-kacheln" role="group" aria-label={t("Schnellfilter", "Quick filters")}>
            {kachel("ueberfaellig", t("Überfällig", "Overdue"), n.overdue ? "tk-koop-ueberfaellig" : "")}
            {kachel("woche", t("Fällig diese Woche", "Due this week"), "")}
            {kachel("pruefung", t("Zur Prüfung", "For review"), "")}
            {kachel("marke", t("Bei der Marke", "With the brand"), "")}
            {kachel("kennzeichnung", t("Kennzeichnung prüfen", "Check ad disclosure"), n.live_unchecked ? "tk-koop-ueberfaellig" : "")}
          </div>
          <Toolbar
            search={suche}
            onSearch={(v) => setParam("q", v)}
            label={t("Beiträge durchsuchen", "Search posts")}
            placeholder={t("Suchen …", "Search …")}
            filters={FILTER_REIHE.map((f) => ({ key: f, label: FILTER_LABEL[f], count: zahl(f) }))}
            filter={filter}
            onFilter={(f) => setParam("filter", f === "offen" ? "" : f)}
          >
            <select hidden={ws.isCreator} aria-label={t("Nach Creator filtern", "Filter by creator")} value={creator} onChange={(e) => setParam("creator", e.target.value)}>
              <option value="">{t("Alle Creator", "All creators")}</option>
              {d.talents.filter((t) => creatorIds.has(t.id) || t.id === creator).map((t) => <option key={t.id} value={t.id}>{nameOfTalent(t)}</option>)}
            </select>
            <select aria-label={t("Nach Marke filtern", "Filter by brand")} value={marke} onChange={(e) => setParam("marke", e.target.value)}>
              <option value="">{t("Alle Marken", "All brands")}</option>
              {d.brands.filter((b) => markenIds.has(b.id) || b.id === marke).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Toolbar>

          {gezeigt.length === 0 ? (
            <p className="muted" role="status">{filter === "ueberfaellig" ? t("Nichts überfällig.", "Nothing overdue.") : filter === "kennzeichnung" ? t("Alle Beiträge, die live sind, haben eine geprüfte Kennzeichnung.", "All posts that are live have a checked ad disclosure.") : t("Keine Beiträge in dieser Auswahl.", "No posts in this selection.")}</p>
          ) : (
            <div className="tablewrap tk-koop-karten tk-koop-schmal">
              <table>
                <caption className="sr-only">{t("Beiträge · ", "Posts · ")}{FILTER_LABEL[filter]}</caption>
                <thead>
                  <tr><th scope="col">{t("Beitrag", "Post")}</th><th scope="col">{t("Kooperation", "Deal")}</th><th scope="col">{t("Fristen", "Deadlines")}</th><th scope="col">{t("Stand", "Status")}</th><th scope="col">{t("Marke", "Brand")}</th><th scope="col">{t("Link", "Link")}</th></tr>
                </thead>
                <tbody>
                  {gezeigt.map((z) => (
                    <tr key={z.row.id}>
                      <td className="tk-koop-kopf">
                        <Link to={`/agentur/kooperationen/${z.deal.id}?beitrag=${z.row.id}`}><strong>{z.name}</strong></Link>
                        {z.month && <small>{monthText(z.month)}</small>}
                      </td>
                      <td data-label={t("Kooperation", "Deal")} className="tk-koop-zelle">
                        {z.deal.title}
                        <small>{z.talent} · {z.brand}</small>
                        {!z.aktiv && z.row.status !== "live" ? <small>{t("ohne laufende Fristen", "no running deadlines")}</small> : null}
                      </td>
                      <td data-label={t("Fristen", "Deadlines")}>{fristText(z.row)}</td>
                      <td data-label={t("Stand", "Status")}><PillView pill={beitragPill(z.row, z.aktiv ? today : "0000-01-01")} /></td>
                      <td data-label={t("Marke", "Brand")}>
                        {z.marke
                          ? <><Link to={`/agentur/kooperationen/${z.deal.id}/content?beitrag=${z.row.id}`}>{t("Bei der Marke", "With the brand")}</Link>
                              <small>{t("seit ", "for ")}{date(z.marke.created_at)} · {z.marke.first_opened_at ? t("geöffnet", "opened") : t("noch nicht geöffnet", "not opened yet")}</small></>
                          : <span className="muted">–</span>}
                      </td>
                      <td data-label={t("Link", "Link")}>
                        {z.row.live_url ? <a href={z.row.live_url} target="_blank" rel="noreferrer noopener">{t("Öffnen ↗", "Open ↗")}</a> : <span className="muted">–</span>}
                        {z.row.status === "live" && !z.row.label_checked && <small className="tk-koop-ueberfaellig">{t("Kennzeichnung prüfen", "Check ad disclosure")}</small>}
                        {z.row.status === "live" && z.row.label_checked && z.row.label_checked_at && <small>{t("✓ geprüft ", "✓ checked ")}{date(z.row.label_checked_at)}</small>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="source-note">{t("Überfällig heißt: Der Termin der Veröffentlichung ist vorbei und der Beitrag nicht live, oder die Entwurfsfrist ist vorbei und noch nichts eingereicht. Abgeschlossene, angehaltene, stornierte und archivierte Kooperationen haben keine laufenden Fristen.", "Overdue means: the publication date has passed and the post is not live, or the draft deadline has passed and nothing has been submitted yet. Completed, paused, cancelled and archived deals have no running deadlines.")}</p>
        </>
      )}
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}
    </main>
  );
}
